import React, { useEffect, useState } from "react";
import { Table, Card, Spinner, Badge, Button, Modal } from "react-bootstrap";
import moment from "moment";
import { useParams } from "react-router-dom";
import CustomPagination from "components/shared/Pagination";
import { getAssignmentAttempts } from "services/Teacher/Assignments.service";
import PageHeading from "components/shared/PageHeading";
import { useTranslation } from "react-i18next";

const VocabularySubmissions = () => {
  const { t } = useTranslation();
  const { assignmentId } = useParams();
  const params = useParams()
  const [loading, setLoading] = useState(true);
  const [assignmentData, setAssignmentData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false); // Modal state
  const [selectedAttempt, setSelectedAttempt] = useState(null); // Selected attempt for modal
  const exerciseIdParams = assignmentId ? assignmentId : params.id
  // Fetch assignment attempts
  const fetchAttempts = async (page = 1) => {
    setLoading(true);
    try {
      const response = await getAssignmentAttempts(exerciseIdParams, page);
      if (response?.data?.results) {
        setAssignmentData(response.data.results);
        setTotalPages(response.data.results.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching attempts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttempts(currentPage);
  }, [currentPage]);

  // Function to format stage titles
  const getStageTitle = (stageKey) => {
    const { sourceLanguage, targetLanguage } = assignmentData || {};
    switch (stageKey) {
      case "sourceToTarget":
        return `${sourceLanguage} to ${targetLanguage}`;
      case "targetToSource":
        return `${targetLanguage} to ${sourceLanguage}`;
      case "spelling":
        return "Spelling";
      default:
        return stageKey;
    }
  };

  // Function to format status
  const formatStatus = (status) =>
    status === "in-progress" ? "In Progress" : status === "completed"? "Completed":"Pending";

  // Function to handle "View Details" button click
  const handleViewDetails = (attempt) => {
    setSelectedAttempt(attempt);
    setShowModal(true);
  };

  // Function to close the modal
  const handleCloseModal = () => setShowModal(false);

  return (
    <div>
      <PageHeading
        heading="Assignment Submissions"
        spanText={
          assignmentData?.studySetTitle && assignmentData.studySetTitle !== "N/A"
            ? `— ${assignmentData.studySetTitle}`
            : ""
        }
      />

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p>{t("Loading submissions...")}</p>
        </div>
      ) : (
        <>
          {/* Assignment Info Card */}
          <Card className="shadow-sm p-3 mb-4">
            <Card.Body>
              <h5 className="mb-1">
                <strong>{t("Assignment")}:</strong> {assignmentData?.studySetTitle}
              </h5>
              <p className="mb-1">
                <strong>{t("Assigned By")}:</strong> {assignmentData?.assignedBy}
              </p>
              <p className="mb-0">
                <strong>{t("Total Attempts")}:</strong> {assignmentData?.totalAttempts}
              </p>
            </Card.Body>
          </Card>

          {/* Attempts Table */}
          <Table bordered hover responsive className="align-middle">
            <thead className="bg-light">
              <tr>
                <th>{t("Student Name")}</th>
                <th>{t("Email")}</th>
                <th>{t("Quiz Attempts")}</th>
                <th>{t("Current Stage")}</th>
                <th>{t("Status")}</th>
                {/* <th>Last Attempt</th> */}
                <th>{t("Completed Words")}</th>
                <th>{t("Mastered Words")}</th>
                <th>{t("Stats")}</th>
              </tr>
            </thead>
            <tbody>
              {assignmentData?.attempts.length > 0 ? (
                assignmentData.attempts.map((attempt) => (
                  <tr key={attempt.studentId}>
                    <td>{attempt.studentName}</td>
                    <td>{attempt.email}</td>
                    <td>{attempt.quizAttempts}</td>
                    <td>
                      <Badge bg="info" className="px-3 py-2">
                        {getStageTitle(attempt.currentStage)}
                      </Badge>
                    </td>
                    <td>
                      <Badge
                        bg={attempt.status === "completed" ? "success" : "warning"}
                        className="px-3 py-2"
                      >
                        {formatStatus(attempt.status)}
                      </Badge>
                    </td>
                    {/* <td>{moment(attempt.lastAttemptAt).format("DD MMM YYYY, h:mm A")}</td> */}
                    <td>{attempt.completedWordsCount}</td>
                    <td>{attempt.masteredWordsCount}</td>
                    <td>
                      <Button
                        variant="primary"
                        className="button-style"
                        size="sm"
                        onClick={() => handleViewDetails(attempt)}
                      >
                        {t("View Details")}
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center">
                    {t("No attempts found.")}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {/* Pagination Component */}
          <CustomPagination
            loading={loading}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* Modal for Detailed Stats */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
  <Modal.Header closeButton>
    <Modal.Title>{t("Stage Details")}</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    {selectedAttempt && (
      <div>
        <h5>{t("Student")}: {selectedAttempt.studentName}</h5>
        <h6>{t("Current Stage")}: {getStageTitle(selectedAttempt.currentStage)}</h6>
          {Object.keys(selectedAttempt.stages).map((stageKey) => {
            const stage = selectedAttempt.stages[stageKey];
            return (
                <Card className="mb-3 px-3" key={stageKey}>
                <div className="d-flex justify-content-between mt-2">
                <strong>{getStageTitle(stageKey)}</strong>

                    <Badge
                        bg={stage.status === "completed" ? "success" : "warning"}
                        className="px-3 py-2"
                      >
                        {formatStatus(stage.status)}
                      </Badge>
                </div>
                <div className="my-2 text-center">
                  <h5>{t("Highest Score")}: {stage.score}</h5>{" "}
                </div>
                </Card>
            );
          })}
      </div>
    )}
  </Modal.Body>
</Modal>
    </div>
  );
};

export default VocabularySubmissions;
